import React, { forwardRef, useCallback } from 'react';

import { link } from '@aline/routes';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { SearchX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineButton } from 'src/components/generic';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';
import { makeStyles } from 'tss-react/mui';

export const NOT_FOUND_ENTITIES = {
  chat: 'chat',
  document: 'document',
  workflow: 'workflow',
} as const;

export type NotFoundEntity = (typeof NOT_FOUND_ENTITIES)[keyof typeof NOT_FOUND_ENTITIES];

export interface NotFoundAction {
  readonly label: string;
  readonly onClick: () => void;
}

/**
 * Not in `appColors` — the design uses a text colour the palette has no token
 * for, already spelled literally elsewhere (see `IntakeFormBuilderModal`).
 */
const TEXT_COLOR = '#374951';
const DESCRIPTION_OPACITY = 0.7;
const NOT_FOUND_COPY: Record<NotFoundEntity, { description: string; title: string }> = {
  chat: {
    description: "We couldn't find the chat you're looking for. It may have been deleted.",
    title: 'Chat not found',
  },
  document: {
    description: "We couldn't find the document you're looking for. It may have been deleted.",
    title: 'Document not found',
  },
  workflow: {
    description: "We couldn't find the workflow you're looking for. It may have been deleted.",
    title: 'Workflow not found',
  },
};

const useStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
  description: {
    color: TEXT_COLOR,
    fontSize: 20,
    fontWeight: 400,
    lineHeight: '32px',
    maxWidth: 520,
    opacity: DESCRIPTION_OPACITY,
  },
  glyph: {
    alignItems: 'center',
    color: TEXT_COLOR,
    display: 'flex',
    fontSize: 64,
    fontWeight: 700,
    gap: theme.spacing(0.5),
    lineHeight: 1,
  },
  root: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4, 3),
    textAlign: 'center',
    width: '100%',
  },
  title: {
    color: TEXT_COLOR,
    fontSize: 32,
    fontWeight: 700,
    marginTop: theme.spacing(2),
  },
}));

interface NotFoundStateProps {
  entity: NotFoundEntity;
  /**
   * Replaces the router exits. The docked chat passes these so a missing
   * thread resets or closes the panel instead of leaving the document or
   * workflow underneath. An empty list still suppresses the router exits.
   */
  actions?: readonly NotFoundAction[];
  className?: string;
}

/**
 * Shown when a routed record is missing or the requester cannot see it. Takes
 * the entity rather than free-form strings so the surfaces cannot drift apart
 * in wording. Forwards a ref because the chat panel renders it inside a
 * scroll container that measures its content.
 */
export const NotFoundState = forwardRef<HTMLDivElement, NotFoundStateProps>(function NotFoundState(
  { actions, className, entity },
  ref
): JSX.Element {
  const { classes } = useStyles();
  const { description, title } = NOT_FOUND_COPY[entity];

  return (
    <div ref={ref} className={clsx(classes.root, className)}>
      {/* Announced as a stray "4 4" otherwise; the heading already carries the meaning. */}
      <div aria-hidden className={classes.glyph}>
        <span>4</span>
        <SearchX size={56} strokeWidth={2.5} />
        <span>4</span>
      </div>
      <Typography className={classes.title} component="h1">
        {title}
      </Typography>
      <Typography className={classes.description}>{description}</Typography>
      <NotFoundActions actions={actions} className={classes.actions} />
    </div>
  );
});

/**
 * Whether this tab has an entry behind the current one. React Router keeps its
 * position in the browser stack on `history.state.idx`, starting at 0 on the
 * first entry of a session, and it survives a reload. Index 0 is the
 * pasted-link case, where `navigate(-1)` would do nothing.
 *
 * Not `document.referrer`: an in-app link opened in a new tab has a same-origin
 * referrer and an empty stack. Not `window.history.length`, which counts
 * forward entries and reads as 2 on a freshly pasted URL.
 */
function getHasHistoryBehind(): boolean {
  const { state } = window.history;
  if (!getIsPlainRecord(state)) return false;

  const { idx } = state;
  return typeof idx === 'number' && idx > 0;
}

function NotFoundActions({
  actions,
  className,
}: {
  actions: readonly NotFoundAction[] | undefined;
  className: string;
}): JSX.Element {
  if (actions) return <ProvidedNotFoundActions actions={actions} className={className} />;

  return <RouterNotFoundActions className={className} />;
}

function ProvidedNotFoundActions({
  actions,
  className,
}: {
  actions: readonly NotFoundAction[];
  className: string;
}): JSX.Element {
  return (
    <div className={className}>
      {actions.map((action, index) => (
        <AlineButton
          key={action.label}
          color="primary"
          variant={index === 0 ? 'contained' : 'outlined'}
          onClick={action.onClick}
        >
          {action.label}
        </AlineButton>
      ))}
    </div>
  );
}

function RouterNotFoundActions({ className }: { className: string }): JSX.Element {
  const navigate = useNavigate();
  /**
   * Read once during render rather than inside the handler, so the button is
   * only offered when it can actually move. A "Go back" that quietly lands on
   * the homepage is the same dead end this screen exists to replace.
   */
  const hasHistoryBehind = getHasHistoryBehind();
  const handleGoBack = useCallback(() => navigate(-1), [navigate]);
  const handleGoHome = useCallback(() => navigate(link.Home()), [navigate]);

  return (
    <div className={className}>
      {hasHistoryBehind && (
        <AlineButton color="primary" variant="contained" onClick={handleGoBack}>
          Go Back
        </AlineButton>
      )}
      <AlineButton
        color="primary"
        variant={hasHistoryBehind ? 'outlined' : 'contained'}
        onClick={handleGoHome}
      >
        Go Home
      </AlineButton>
    </div>
  );
}
